import React, { useMemo } from "react";
import { z } from "zod";
import { SbBlokData } from "@storyblok/js";
import {
  withGlobalStory,
  WithGlobalStoryProps,
} from "../../utils/with-global-story";
import {
  getStoriesFromGlobalStoryblokStoriesByComponentType,
  getStoryFromGlobalStoryblokStoriesBySlug,
} from "../../utils/global-story-functions";
import { TopbarProps } from "@made-people/blueprint-topbar";
import { StoryblokStoryContentResponseV1 } from "@made-people/nestjs-bff-service";
import { TopbarTrustpilot } from "./topbar-trustpilot";
import { TopbarPaymentLogos } from "./topbar-payment-logos";
import { getExistingContent } from "./topbar-content";
import { useShippingThreshold } from "../../hooks/use-shipping-threshold";

const topbarBackgroundColors = ["default", "red", "purple", "black"] as const;

type TopbarBackgroundColor = (typeof topbarBackgroundColors)[number];

/** Maps our Storyblok background colour options to the Blueprint fade theme keys. */
const themeByBackgroundColor: Record<TopbarBackgroundColor, string> = {
  default: "primary",
  red: "secondary",
  purple: "tertiary",
  black: "black",
};

const topbarStorySchema = z.object({
  settings_content: z.array(z.unknown()).optional(),
  backgroundColor: z.enum(topbarBackgroundColors).optional().catch(undefined),
});

/**
 * "default" is the light background (dark text); every other colour is dark, so
 * sibling elements that live outside the Blueprint wrapper (the flag trigger and
 * the payment logo images) need to switch to white.
 */
export const topbarHasDarkBackground = (
  globalStoryblokStories?: StoryblokStoryContentResponseV1[],
): boolean => {
  const stories = getStoriesFromGlobalStoryblokStoriesByComponentType(
    "Topbar",
    globalStoryblokStories,
  );
  const storyResult = topbarStorySchema.safeParse(stories?.[0]);
  const backgroundColor = storyResult.success
    ? storyResult.data.backgroundColor
    : undefined;
  return backgroundColor !== undefined && backgroundColor !== "default";
};

const sbBlokDataSchema = z.custom<SbBlokData>(
  (val) =>
    typeof val === "object" &&
    val !== null &&
    "_uid" in val &&
    "component" in val,
);

const partnerLogosStorySchema = z.object({
  logos: z
    .array(
      z.object({
        logos: z.array(sbBlokDataSchema).optional(),
      }),
    )
    .optional(),
});

const BaseComponent = withGlobalStory(
  {
    lookupType: "componentType",
    componentType: "Topbar",
  },
  (story) =>
    ({
      settings: { template: "fade" },
      ...story,
    }) satisfies TopbarProps,
);

export const Topbar = (props: WithGlobalStoryProps) => {
  const { thresholdEnabled, threshold } = useShippingThreshold();
  const stories = getStoriesFromGlobalStoryblokStoriesByComponentType(
    "Topbar",
    props.globalStoryblokStories,
  );
  const storyResult = topbarStorySchema.safeParse(stories?.[0]);
  const story = storyResult.success ? storyResult.data : undefined;
  const backgroundColor = story?.backgroundColor ?? "default";
  const theme = themeByBackgroundColor[backgroundColor];
  const hasDarkBackground = backgroundColor !== "default";

  const partnerLogosRaw = getStoryFromGlobalStoryblokStoriesBySlug(
    "shared-content/partner-logos/partner-logos",
    props.globalStoryblokStories,
  );
  const partnerLogosResult = partnerLogosStorySchema.safeParse(partnerLogosRaw);
  const logos = partnerLogosResult.success
    ? (partnerLogosResult.data.logos?.[0]?.logos ?? [])
    : [];

  const customItems: Array<{ customContent: React.ReactNode }> = [];

  customItems.push({
    customContent: <TopbarTrustpilot />,
  });

  if (logos.length > 0) {
    customItems.push({
      customContent: (
        <TopbarPaymentLogos logos={logos} invert={hasDarkBackground} />
      ),
    });
  }
  const existingContent = useMemo(
    () =>
      getExistingContent(story?.settings_content, thresholdEnabled, threshold),
    [story?.settings_content, thresholdEnabled, threshold],
  );

  return (
    <BaseComponent
      {...props}
      additionalProps={{
        settings_content: [...existingContent, ...customItems],
        settings_variants_theme: theme,
      }}
    />
  );
};
